import Component from "./component";

const TextAutomation = class extends Component {
  constructor() {
    super( {
      name: "TextAutomation"
    } );

    /**
     * Whether the text has finished displaying all chunks.
     * @private
     */
    this._completedAll = true;

    /**
     * Whether the text has finished displaying the current chunk.
     * @private
     */
    this._completedChunk = true;

    /**
     * Keycodes used to "continue" to the next page of text.
     * @private
     */
    this._continueKeycodes = [];

    /**
     * Whether the text output should use input prompts to control
     * text animation and pagination.
     * @private
     */
    this._enablePages = false;

    /**
     * Whether the text output should animate with typewriter tools.
     * @private
     */
    this._enableRate = false;

    /**
     * Current owner.
     * @private
     */
    this._owner = null;

    /**
     * Full text from which the current display is derived
     * @private
     */
    this._text = "";

    /**
     * Text chunks to display sequentially
     * @private
     */
    this._textChunks = [];

    /**
     * Current text chunk being displayed
     * @private
     */
    this._textChunksIndex = 0;

    /**
     * Current text to display
     * @private
     */
    this._textCurrent = "";

    /**
     * Interval between typewriter characters, in milliseconds
     * @default 10
     */
    this.typewriterInterval = 10;

    /**
     * Symbols rendered in the current typewritten chunk.
     * @private
     */
    this._typewriterSteps = 0;

    /**
     * Time available for typewriting. Some milliseconds may be left over
     * after a frame finishes.
     * @private
     */
    this._typewriterTicks = 0;
  }

  /**
   * Keycodes used to "continue" to the next page of text.
   * @readonly
   */
  get continueKeycodes() {
    return this._continueKeycodes.slice();
  }

  /**
   * Whether the text output should use input prompts to control
   * text animation and pagination.
   */
  set enablePages( value ) {
    value = !!value;
    if ( this._enablePages === value ) {
      return;
    }

    this._enablePages = value;

    this.assignChunks();
  }
  get enablePages() {
    return this._enablePages;
  }

  /**
   * Whether the text output should animate using typewriter tools
   */
  set enableRate( value ) {
    this._enableRate = !!value;
  }
  get enableRate() {
    return this._enableRate;
  }

  set owner( value ) {
    if ( this._owner === value ) {
      // Don't trigger changes if it's not changing.
      return;
    }
    const D = Phaser.GameObjects.Events.DESTROY;
    if ( this._owner ) {
      // Remove destruction listeners from previous owner.
      this._owner.off( D, this.destroy, this );
    }

    this._owner = value;

    if ( this._owner ) {
      // Set up destruction listeners.
      this._owner.once( D, this.destroy, this );
    }
  }
  get owner() {
    return this._owner;
  }

  /**
   * Text for display. Depending on rate and pagination options, this may be
   * split up into page chunks, and/or not displayed all at once.
   * The getter returns whatever is currently on screen.
   */
  set text( value ) {
    this._text = value;

    this.assignChunks();
    this._startPage();
  }
  get text() {
    return this._textCurrent;
  }

  /**
   * Rebuild the display chunk list.
   * Because we're usually going between one and several chunks,
   * we reset associated values.
   */
  assignChunks() {
    this._completedAll = false;
    this._completedChunk = false;
    this._textChunksIndex = 0;
    this._typewriterSteps = 0;
    this._typewriterTicks = 0;
    if ( this._enablePages ) {
      this._textChunks = this._text.split( "[CONTINUE]" );
    } else {
      this._textChunks[ 0 ] = this._text;
    }
  }

  /**
   * Emit events associated with the start of a page display.
   */
  _startPage() {
    // Initialize text immediately.
    if ( this._enableRate ) {
      this._textCurrent = "";
    } else {
      this._textCurrent = this._textChunks[ this._textChunksIndex ];
    }
    this.owner.text = this._textCurrent;

    this.owner.scene.events.emit( "TextAutomationStartPage", this.owner );
  }

  /**
   * Mark the current chunk completed. If it is the last chunk,
   * mark all text as completed.
   * 
   * Also emit events to notify completion.
   */
  completeChunk() {
    this._completedChunk = true;
    this.owner.scene.events.emit( "TextAutomationCompletePage", this.owner );

    if ( this._textChunksIndex === this._textChunks.length - 1 ) {
      this._completedAll = true;
      this.owner.scene.events.emit( "TextAutomationCompleteAll", this.owner );
    }
  }

  /**
   * Clean up when the owner is destroyed.
   */
  destroy() {
    this._continueKeycodes.forEach( keycode => {
      this.setContinueKey( keycode, false );
    } );
  }

  /**
   * Standard component update. Run effects and update text display.
   * The preUpdate phase is the earliest we can fire; it will provide
   * out-of-date data to any instance that preupdates first.
   * @param {Number} time - game time
   * @param {Number} delta - frame duration
   */
  preUpdate( time, delta ) {
    if ( this._completedAll || this._completedChunk ) {
      // Don't do anything if the text has finished displaying.
      return;
    }

    const textCurrent = this._textChunks[ this._textChunksIndex ];
    if ( this._enableRate ) {
      this._typewriterTicks += delta;
      this._textCurrent = this.typewrite( textCurrent );

      if ( this._textCurrent.length === textCurrent.length ) {
        this.completeChunk();
      }
    } else {
      this._textCurrent = textCurrent;
      this.completeChunk();
    }

    // Render.
    this.owner.text = this._textCurrent;
  }

  /**
   * Setup a continuation key to move from page to page.
   * @param {Number} keycode - keycode of the key to set
   * @param {Boolean} enable - whether to enable the key
   */
  setContinueKey( keycode, enable ) {
    const keycodeIndex = this._continueKeycodes.indexOf( keycode );
    if ( enable ) {
      if ( keycodeIndex > -1 ) {
        // Don't add the keycode twice.
        return;
      }
    } else {
      if ( keycodeIndex === -1 ) {
        // Don't remove keycodes that aren't there.
        return;
      }
    }

    this._continueKeycodes.push( keycode );

    this.owner.scene.getKey( keycode )
      [ enable ? "on" : "off" ]( "up", this.showNextPage, this );
  }

  /**
   * Display the next chunk of text.
   * 
   * If text is currently typewriting, emit "TextAutomationFastForward",
   * and fill out the rest of the text.
   */
  showNextPage() {
    if ( this._completedChunk ) {
      if ( this._textChunksIndex === this._textChunks.length - 1 ) {
        // Don't proceed if we're on the last page.
        return;
      }
      // Go to the next page.
      this._completedChunk = false;
      this._textChunksIndex++;
      this._typewriterSteps = 0;
      this._typewriterTicks = 0;
      this._startPage();
    } else {
      // Complete the current typewrite.
      this._typewriterSteps =
        this._textChunks[ this._textChunksIndex ].length;
      this.owner.scene.events.emit( "TextAutomationFastForward", this.owner );
    }
  }

  /**
   * Return text, given the current time conditions.
   * Available milliseconds from _typewriterTicks are consumed to add
   * to the total text amount.
   * @param {String} text - text to extract from
   * @returns {String}
   */
  typewrite( text ) {
    const steps =
      Math.floor( this._typewriterTicks / this.typewriterInterval );
    this._typewriterTicks -= steps * this.typewriterInterval;
    this._typewriterSteps += steps;
    return text.slice( 0, this._typewriterSteps );
  }
};

export default TextAutomation;
